import axios from "axios"
import { auth } from "@/lib/firebase"
import { setAppError } from "@/store/init/initReducer"
import { toast } from "@/hooks/use-toast"

const httpState = {
  token: null as string | null,
}

const http = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
})

http.interceptors.request.use(async (config) => {
  let _token: string | null = null
  try {
    _token = localStorage.getItem("token")
  } catch (err) {
    // DO NOTHING
  }

  if (httpState.token || _token) {
    config.headers.Authorization = `Bearer ${httpState.token || _token}`
  } else {
    try {
      const currentUser = auth.currentUser
      if (currentUser) {
        const idToken = await currentUser.getIdToken()
        config.headers.Authorization = `Bearer ${idToken}`
        httpState.token = idToken
        localStorage.setItem("token", idToken)
      }
    } catch (err) {
      console.warn("Failed to attach auth token:", err)
    }
  }
  return config
})

export const setHttpAuth = ({ token, logout = false }: { token?: string; logout?: boolean }) => {
  if (logout) {
    httpState.token = null
    localStorage.removeItem("token")
  } else {
    if (token) {
      httpState.token = token
      localStorage.setItem("token", token)
    }
  }
}

export default http

const getRefreshToken = async () => {
  try {
    const currentUser = auth.currentUser;
    if (currentUser) {
      try {
        // Check if token is about to expire (refresh 5 minutes before expiry)
        const tokenResult = await currentUser.getIdTokenResult()
        const expirationTime = new Date(tokenResult.expirationTime).getTime()
        const currentTime = Date.now()
        const timeUntilExpiry = expirationTime - currentTime
        // console.log('checking token', { currentTime, expirationTime, timeUntilExpiry })

        // If token expires in less than 5 minutes, refresh it
        if (timeUntilExpiry < 5 * 60 * 1000) {
          console.log("Token expiring soon, refreshing...")
          const idToken = await currentUser.getIdToken(true) // Force refresh
          setHttpAuth({ token: idToken })
        } else {
          setHttpAuth({ token: tokenResult.token })
        }
      } catch (error) {
        console.error("Token check error:", error)
      }
    } else {
      console.log('No Current User found');
    }
    return null;
  } catch (err: any) {
    console.log('Failed to refresh token', err);
    return null;
  }
}

/**
 * Surfaces a failed request to the operator.
 *
 * Every helper below returns `null` on failure, and only a literal "Network
 * Error" used to reach the UI — a 400, 401, 403 or 500 was written to the
 * console and nothing else. Callers then reported success regardless, so a
 * rejected save looked exactly like a successful one and the change simply
 * never appeared on the student site.
 *
 * It also raises a toast rather than relying on `setAppError` alone: that
 * action writes `init.error`, and no component reads it, so even the one
 * message that did fire ("Server is down") was never shown to anybody. The
 * dispatch is kept so the state stays accurate for anything that reads it
 * later; the toast is what the operator actually sees.
 */
const reportRequestFailure = (action: string, err: any, dispatch: any) => {
  console.error(`Failed to ${action}`, err);

  const show = (title: string, description: string) => {
    dispatch(setAppError(description));
    toast({ title, description, variant: "destructive" });
  }

  if ((err?.message || '').includes("Network Error")) {
    show("Server unreachable", "Server is down. Please try again later. Nothing was saved.");
    return;
  }

  const status = err?.response?.status;

  if (status === 401 || status === 403) {
    show("Not signed in", "Your session has expired or you do not have permission. Sign in again.");
    return;
  }

  const reason = err?.response?.data?.message || err?.message || "Unknown error";
  show(
    `Could not ${action}`,
    status ? `${reason} (HTTP ${status}). Nothing was saved.` : `${reason}. Nothing was saved.`
  );
}

export const httpPost = async ({ url, payload, dispatch }: { url: string; payload: any; dispatch: any }) => {
  try {
    await getRefreshToken();
    const res = await http.post(url, payload);

    return res;
  } catch (err: any) {
    reportRequestFailure('save', err, dispatch);
    return null;
  }
}

export const httpPostFile = async ({ url, file, dispatch }: { url: string; file: File; dispatch: any }) => {
  try {
    await getRefreshToken();
    const formData = new FormData();
    formData.append("file", file);
    const res = await http.post(url, formData, {
      timeout: 0, // as this might take more time to upload then usual requests
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return res;
  } catch (err: any) {
    reportRequestFailure('upload the file', err, dispatch);
    return null;
  }
}

export const httpGet = async ({ url, dispatch, responseType = 'json' }: { url: string; dispatch: any, responseType?: 'blob' | 'json' }) => {
  try {
    await getRefreshToken();
    const res = await http.get(url, {
      responseType,
    });

    return res;
  } catch (err: any) {
    reportRequestFailure('load that data', err, dispatch);
    return null;
  }
}

export const httpDelete = async ({ url, dispatch }: { url: string; dispatch: any }) => {
  try {
    await getRefreshToken();
    const res = await http.delete(url);

    return res;
  } catch (err: any) {
    // The 401 branch used to sit on a bare `if` after the network-error `if`,
    // so a network error fell through into it and reloaded the page.
    if (err?.response?.status === 401) {
      dispatch(setAppError("Session Expired. Refreshing..."));
      setTimeout(() => {
        window.location.reload();
      }, 3000)
      return null;
    }
    reportRequestFailure('delete that', err, dispatch);
    return null;
  }
}


export const serializedError = (err: any) => ({
  message: err?.response?.data?.message || err.message || "Failed to fetch question bank",
  status: err?.response?.status,
})
